import React from "react";
import { graphql } from "react-relay";

import { faCalendar } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { format } from "date-fns";
import { fetchQuery } from "relay-runtime";

import { EventFormContext } from "~/calendar/EventForm/EventFormContext";
import type { Participant } from "~/calendar/hooks/useParticipantsOptions";
import { environment } from "~/models";
import { Tooltip } from "~/ui";

import { formatTime } from "../utils/meetingGuidelinesUtils";

import type { AttendeeWeeklyHoursInMinutesQuery } from "~/__generated__/AttendeeWeeklyHoursInMinutesQuery.graphql";

import styles from "./AttendeeWeeklyHours.module.css";

interface AttendeeWeeklyHoursProps {
	attendee: Participant;
}

const attendeeMinutesInMeetings = graphql`
	query AttendeeWeeklyHoursInMinutesQuery($userId: ID!, $currentDate: Date!) {
		user: node(id: $userId) {
			... on User {
				weeklyMinutesInMeetings(currentDate: $currentDate)
				attendeeWeeklyMeetingHoursLimit
			}
		}
	}
`;

export default function AttendeeWeeklyHours({ attendee }: AttendeeWeeklyHoursProps) {
	const [minutesInMeetings, setMinutesInMeetings] = React.useState<number>(0);
	const [weeklyMeetingHoursLimit, setWeeklyMeetingHoursLimit] = React.useState<number>(72000);
	const [weeklyMeetingHoursEnabled, setWeeklyMeetingHoursEnabled] = React.useState<boolean>(false);

	const { timeState: timeState } = React.useContext(EventFormContext);

	const MAX_ATTENDEE_WEEKLY_MEETING_MINUTES = weeklyMeetingHoursLimit / 60;

	const isThisMeetingGuidelineEnabled = useFeatureIsOn("extension-prompts") && weeklyMeetingHoursEnabled;

	React.useEffect(() => {
		if (!attendee.user?.id || !attendee.hasConfiguredUser) return;

		const currentDate = format(timeState.start, "yyyy-MM-dd");

		fetchQuery<AttendeeWeeklyHoursInMinutesQuery>(environment, attendeeMinutesInMeetings, {
			userId: attendee.user?.id,
			currentDate: currentDate,
		})
			.toPromise()
			.then(response => {
				const responseMinutesInMeetings = response?.user?.weeklyMinutesInMeetings;
				setMinutesInMeetings(responseMinutesInMeetings ?? 0);
				const responseWeeklyMeetingHoursLimit = response?.user?.attendeeWeeklyMeetingHoursLimit;
				setWeeklyMeetingHoursLimit(responseWeeklyMeetingHoursLimit ?? 72000);
				const responseWeeklyMeetingHoursEnabled = typeof response?.user?.attendeeWeeklyMeetingHoursLimit === "number";
				setWeeklyMeetingHoursEnabled(responseWeeklyMeetingHoursEnabled);
			});
	}, [attendee.hasConfiguredUser, attendee.user?.id, timeState.start]);

	if (!isThisMeetingGuidelineEnabled || minutesInMeetings < MAX_ATTENDEE_WEEKLY_MEETING_MINUTES) return null;

	return (
		<Tooltip content={`Attendee has ${formatTime(minutesInMeetings)} of meetings this week`}>
			<div className={styles.weeklyHours}>
				<span>
					{Math.floor(minutesInMeetings / 60)} hrs
					{minutesInMeetings % 60 !== 0 && " +"}
				</span>
				<span className={styles.weeklyHoursIcon}>
					<FontAwesomeIcon icon={faCalendar} />
				</span>
			</div>
		</Tooltip>
	);
}
